import axios from "axios";
import apiUrl from "../../../Apiurl";

const base = `${apiUrl}/moda/kit-moda`;
const perArticolo = (codVS) => `${base}/${encodeURIComponent(codVS)}`;

export const messaggioErrore = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

export const isAnnullata = (error) =>
  error?.name === "CanceledError" || error?.name === "AbortError" || axios.isCancel?.(error);

/**
 * Chiamate della tab Kit Moda. Il token viene rinnovato a ogni chiamata; le
 * scritture passano lo username Domino sia nell'header sia nel body.
 */
export function creaApiKitModa({ getAccessTokenSilently, username }) {
  const headers = async (scrittura = false) => {
    const token = await getAccessTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
      ...(scrittura ? { "X-Domino-Username": username } : {}),
    };
  };
  const dati = (response) => response.data?.data ?? null;

  return {
    async anteprimaImport(snapshot, signal) {
      return datiImport(await axios.post(`${base}/import/anteprima`, formImportDaSnapshot(snapshot), {
        headers: await headers(true), signal,
      }));
    },
    async avviaImport(snapshot) {
      return datiImport(await axios.post(`${base}/import/job`, formImportDaSnapshot(snapshot, true), {
        headers: await headers(true),
      }));
    },
    async statoImport(jobId, signal) {
      return datiImport(await axios.get(`${base}/import/job/${encodeURIComponent(jobId)}`, {
        headers: await headers(), signal, timeout: 20000,
      }));
    },
    async stato(codVS, signal) {
      return dati(await axios.get(perArticolo(codVS), { headers: await headers(), signal }));
    },
    async verifica(codVS, triplette) {
      return dati(
        await axios.post(
          `${perArticolo(codVS)}/verifica`,
          { updatedBy: username, ...(triplette ? { triplette } : {}) },
          { headers: await headers(true) },
        ),
      );
    },
    async salvaComposizione(codVS, componenti) {
      return dati(
        await axios.put(
          `${perArticolo(codVS)}/composizione`,
          { updatedBy: username, componenti },
          { headers: await headers(true) },
        ),
      );
    },
    /** `forza`: scrive anche se il kit su Prestashop non e' piu' quello mostrato ("Invia comunque"). */
    async salvaTaglia(codVS, tripletta, componenti, attesi, forza = false) {
      return dati(
        await axios.put(
          `${perArticolo(codVS)}/taglie/${encodeURIComponent(tripletta)}`,
          { updatedBy: username, componenti, attesi, ...(forza ? { forza: true } : {}) },
          { headers: await headers(true) },
        ),
      );
    },
    async cerca(testo, signal) {
      return (
        dati(
          await axios.get(`${base}/componenti/cerca`, {
            params: { q: testo },
            headers: await headers(),
            signal,
          }),
        ) ?? []
      );
    },
    async taglie(codVS, signal) {
      return (
        dati(
          await axios.get(`${base}/componenti/${encodeURIComponent(codVS)}/taglie`, {
            headers: await headers(),
            signal,
          }),
        ) ?? []
      );
    },
  };
}

// Anteprima e invio usano lo stesso File e gli stessi parametri, senza rileggere il form.
function formImportDaSnapshot(snapshot, job = false) {
  const form = new FormData();
  form.append("uploaded_file", snapshot.file);
  form.append("modalita", snapshot.modalita);
  if (snapshot.modalita === "COMPONENTE_SINGOLO") form.append("quantita", snapshot.quantita);
  form.append("updatedBy", snapshot.updatedBy);
  if (job) form.append("sovrascrivi", JSON.stringify(snapshot.sovrascrivi));
  return form;
}

function datiImport(response) {
  if (response.data?.success === false) throw new Error(response.data.error || "Import Kit Moda non riuscito");
  if (!response.data?.data) throw new Error("Risposta import Kit Moda non valida");
  return response.data.data;
}

/** "COLORE UNICO / CM 165", oppure il ripiego su variante e ordinale. */
export const etichettaTaglia = (taglia) =>
  [taglia?.varianteDescrizione, taglia?.tagliaDescrizione].filter(Boolean).join(" / ") ||
  `Variante ${taglia?.numeroVariante ?? "?"} / taglia ${taglia?.numeroTaglia ?? "?"}`;

/** Componenti attuali di un kit in cache, nel formato `{ tripletta, quantita }` atteso dal backend. */
export const componentiAttuali = (cache) =>
  cache?.esito === "KIT"
    ? cache.componenti.map((componente) => ({
        tripletta: componente.itemReference,
        quantita: componente.itemsInKit,
      }))
    : [];

/** Risposta 409: il kit su Prestashop non e' piu' quello mostrato; `data` e' lo stato aggiornato. */
export const statoDaConflitto = (error) =>
  error?.response?.status === 409 && error.response.data?.data ? error.response.data.data : null;

/** Una taglia va riletta da Tembo prima di modificarla se manca la cache o la tripletta dei componenti. */
export const richiedeVerifica = (riga) =>
  !riga.cache ||
  (riga.cache.esito === "KIT" &&
    riga.cache.componenti.some((componente) => !componente.itemReference));
